/**
 * The nested Firestore layout, as path builders — the ONE source of truth for where everything lives, used
 * by BOTH the MCP server and the web client (they use the same `firebase/firestore` SDK + anonymous access;
 * they just read/write slightly different data). Keeping the paths here means the layout can't drift between
 * the two clients (or silently break after a storage migration).
 *
 *   pairings/{pid}/sessions/{sid}                    — the session/presence doc
 *     /channels/{cid}         (+ /messages/{mid})    — all conversation; the MAIN chat is channels/main
 *     /state/{settings|todos|mailbox}                — per-session state docs
 *     /callRequests/current                          — reverse-call request (single well-known doc)
 *     /embeddings/{id}                               — content-free semantic-search vectors (per-session)
 *   signaling/{sid}           (+ /candidates/{id})   — ephemeral WebRTC signaling (TTL-reaped)
 */
import { collection, doc, type CollectionReference, type DocumentReference, type Firestore } from "firebase/firestore";

/** The well-known id of the main chat channel (the main chat is just channels/main). */
export const MAIN_CHANNEL = "main";

export function sessionDoc(db: Firestore, pid: string, sid: string): DocumentReference {
  return doc(db, "pairings", pid, "sessions", sid);
}
export function sessionsCol(db: Firestore, pid: string): CollectionReference {
  return collection(db, "pairings", pid, "sessions");
}
export function channelsCol(db: Firestore, pid: string, sid: string): CollectionReference {
  return collection(db, "pairings", pid, "sessions", sid, "channels");
}
export function channelDoc(db: Firestore, pid: string, sid: string, channelId: string): DocumentReference {
  return doc(db, "pairings", pid, "sessions", sid, "channels", channelId);
}
export function channelMsgCol(db: Firestore, pid: string, sid: string, channelId: string): CollectionReference {
  return collection(db, "pairings", pid, "sessions", sid, "channels", channelId, "messages");
}
export function channelMsgDoc(db: Firestore, pid: string, sid: string, channelId: string, messageId: string): DocumentReference {
  return doc(db, "pairings", pid, "sessions", sid, "channels", channelId, "messages", messageId);
}
/** A channel's per-channel DOCUMENTS collection (CHANNELS.md §Documents) — named markdown docs, encrypted
 *  like everything else (e.g. a delivered plan on the main channel). Distinct from the channel's single
 *  `doc` field (the idea spec). */
export function channelDocsCol(db: Firestore, pid: string, sid: string, channelId: string): CollectionReference {
  return collection(db, "pairings", pid, "sessions", sid, "channels", channelId, "documents");
}
/**
 * Review COMMENTS on a channel's documents — one doc per comment, alongside the documents rather than inside
 * them. Documents are immutable (that is what makes revisions work), and a comment has to change: it is
 * resolved individually, and it outlives the revision it was written on. Keyed by the document THREAD, not a
 * single revision, so a comment made on v1 is still there when v2 is posted.
 */
export function channelCommentsCol(db: Firestore, pid: string, sid: string, channelId: string): CollectionReference {
  return collection(db, "pairings", pid, "sessions", sid, "channels", channelId, "comments");
}
export function channelCommentDoc(db: Firestore, pid: string, sid: string, channelId: string, commentId: string): DocumentReference {
  return doc(db, "pairings", pid, "sessions", sid, "channels", channelId, "comments", commentId);
}
/** The BACKLOG collection (CHANNELS.md §Backlog) — the idea lifecycle layer, separate from
 *  the generic channel: `backlog/{channelId}` = { status (draft→ready→in_progress→done), order }. */
export function backlogCol(db: Firestore, pid: string, sid: string): CollectionReference {
  return collection(db, "pairings", pid, "sessions", sid, "backlog");
}
export function backlogDoc(db: Firestore, pid: string, sid: string, channelId: string): DocumentReference {
  return doc(db, "pairings", pid, "sessions", sid, "backlog", channelId);
}
/** Agents the user asked this session to start: `agentRequests/{id}` = encrypted `ct` + `ts`, removed once taken. */
export function agentRequestsCol(db: Firestore, pid: string, sid: string): CollectionReference {
  return collection(db, "pairings", pid, "sessions", sid, "agentRequests");
}
export function agentRequestDoc(db: Firestore, pid: string, sid: string, id: string): DocumentReference {
  return doc(db, "pairings", pid, "sessions", sid, "agentRequests", id);
}
/** Recurring maintenance tasks: `maintenance/{taskId}` = encrypted config `ct` (server) + plaintext
 *  `active` toggle (app). */
export function maintenanceCol(db: Firestore, pid: string, sid: string): CollectionReference {
  return collection(db, "pairings", pid, "sessions", sid, "maintenance");
}
export function maintenanceDoc(db: Firestore, pid: string, sid: string, taskId: string): DocumentReference {
  return doc(db, "pairings", pid, "sessions", sid, "maintenance", taskId);
}
export function channelDocDoc(db: Firestore, pid: string, sid: string, channelId: string, docId: string): DocumentReference {
  return doc(db, "pairings", pid, "sessions", sid, "channels", channelId, "documents", docId);
}

/** Group chat (#Group-chat): multi-participant channels live at the PAIRING level — NOT nested under one
 *  session — so agents in different sessions/repos can all see and be woken by them. `participants` is a list
 *  of sessionIds (+ human/agent kind); the client resolves each to a name/repo from its session data. */
export function groupsCol(db: Firestore, pid: string): CollectionReference {
  return collection(db, "pairings", pid, "groups");
}
export function groupDoc(db: Firestore, pid: string, groupId: string): DocumentReference {
  return doc(db, "pairings", pid, "groups", groupId);
}
export function groupMsgCol(db: Firestore, pid: string, groupId: string): CollectionReference {
  return collection(db, "pairings", pid, "groups", groupId, "messages");
}
export function groupMsgDoc(db: Firestore, pid: string, groupId: string, messageId: string): DocumentReference {
  return doc(db, "pairings", pid, "groups", groupId, "messages", messageId);
}
/** The main chat's messages collection (channels/main/messages). */
export function mainMsgCol(db: Firestore, pid: string, sid: string): CollectionReference {
  return channelMsgCol(db, pid, sid, MAIN_CHANNEL);
}
/** A per-session state doc: `state/{name}` where name ∈ {settings, todos, mailbox}. */
export function stateDoc(db: Firestore, pid: string, sid: string, name: "settings" | "todos" | "mailbox" | "activity" | "agents"): DocumentReference {
  return doc(db, "pairings", pid, "sessions", sid, "state", name);
}
export function callRequestDoc(db: Firestore, pid: string, sid: string): DocumentReference {
  return doc(db, "pairings", pid, "sessions", sid, "callRequests", "current");
}
export function embeddingsCol(db: Firestore, pid: string, sid: string): CollectionReference {
  return collection(db, "pairings", pid, "sessions", sid, "embeddings");
}
export function embeddingDoc(db: Firestore, pid: string, sid: string, id: string): DocumentReference {
  return doc(db, "pairings", pid, "sessions", sid, "embeddings", id);
}
export function signalingDoc(db: Firestore, sessionId: string): DocumentReference {
  return doc(db, "signaling", sessionId);
}
export function signalingCandidates(db: Firestore, sessionId: string): CollectionReference {
  return collection(db, "signaling", sessionId, "candidates");
}
